import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Button from "./Button";
import Card from "./Card";
import ColorInput from "./ColorInput";
import { Field, Input, Select } from "./Field";
import Modal from "./Modal";
import Popover from "./Popover";
import { DotsHorizontalIcon, LinkIcon } from '@heroicons/react/outline';
import { BiListOl, BiListUl } from 'react-icons/bi';
import "./HtmlEditor.css";

const FONT_FAMILIES = [
  { label: 'Sans Serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, Times New Roman, serif' },
  { label: 'Fixed Width', value: 'ui-monospace, monospace' },
  { label: 'Wide', value: 'Arial Black, sans-serif' },
  { label: 'Narrow', value: 'Arial Narrow, sans-serif' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
  { label: 'Garamond', value: 'Garamond, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const FONT_SIZES = [
  { label: 'Small', value: '11px' },
  { label: 'Normal', value: '14px' },
  { label: 'Large', value: '18px' },
  { label: 'Huge', value: '26px' },
];

const isLiveEditor = editor => editor && !editor.isDestroyed;

/** HTML editor presentation. Callers own template data, variable insertion and saving. */
const HtmlEditor = forwardRef(function HtmlEditor({ initialContent = "", onContentChange, placeholder = "", ariaLabel = "Email body", variant, toolbarActions, afterContent }, ref) {
  const debounceRef = useRef(null);
  const initializedRef = useRef(false);
  const formattingRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [displayText, setDisplayText] = useState("");
  const [linkTrigger, setLinkTrigger] = useState(0);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }), Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }), TextStyle, FontSize, Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } }),
      Placeholder.configure({ placeholder }), FontFamily,
      Extension.create({ name: "htmlEditorShortcuts", addKeyboardShortcuts() { return { "Mod-k": () => { setLinkTrigger(value => value + 1); return true; } }; } }),
    ],
    content: initialContent || "",
    editorProps: { attributes: { role: "textbox", "aria-multiline": "true", "aria-label": ariaLabel } },
    onUpdate: ({ editor: current }) => {
      if (!onContentChange) return;
      clearTimeout(debounceRef.current);
      const notify = () => { if (!isLiveEditor(current)) return; const html = current.getHTML(); onContentChange(html === "<p></p>" ? "" : html); };
      if (variant === 'compose') notify();
      else debounceRef.current = setTimeout(notify, 200);
    },
  });
  useEffect(() => {
    if (isLiveEditor(editor) && initialContent && !initializedRef.current) {
      if (["<p></p>", ""].includes(editor.getHTML())) { editor.commands.setContent(initialContent, false); initializedRef.current = true; }
    }
  }, [editor, initialContent]);
  useEffect(() => () => clearTimeout(debounceRef.current), []);
  useImperativeHandle(ref, () => ({
    focus: () => { if (isLiveEditor(editor)) editor.commands.focus(); },
    getHTML: () => isLiveEditor(editor) ? editor.getHTML() : "",
    setContent: html => { if (isLiveEditor(editor)) editor.commands.setContent(html || "", false); },
    getEditor: () => isLiveEditor(editor) ? editor : null,
  }), [editor]);
  const openLink = () => {
    if (!isLiveEditor(editor)) return;
    const { from, to } = editor.state.selection;
    setUrl(editor.getAttributes("link").href || "");
    setDisplayText(editor.state.doc.textBetween(from, to, "") || "");
    setLinkOpen(true);
  };
  useEffect(() => {
    if (!isLiveEditor(editor) || !linkTrigger) return;
    const { from, to } = editor.state.selection;
    setUrl(editor.getAttributes("link").href || "");
    setDisplayText(editor.state.doc.textBetween(from, to, "") || "");
    setLinkOpen(true);
  }, [editor, linkTrigger]);
  const applyLink = () => {
    if (!isLiveEditor(editor)) return;
    if (!url.trim()) editor.chain().focus().unsetLink().run();
    else {
      const href = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
      if (displayText && editor.state.selection.from === editor.state.selection.to) editor.chain().focus().insertContent({ type: "text", text: displayText, marks: [{ type: "link", attrs: { href, target: "_blank" } }] }).run();
      else editor.chain().focus().setLink({ href, target: "_blank" }).run();
    }
    setLinkOpen(false);
  };
  const extendedControls = isLiveEditor(editor) && <>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>Undo</Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>Redo</Button>
      <div className="w-36"><Select size="sm" aria-label="Font family" value={editor.getAttributes("textStyle").fontFamily || FONT_FAMILIES[0].value} onChange={event => editor.chain().focus().setFontFamily(event.target.value).run()} options={FONT_FAMILIES} /></div>
      <div className="w-24"><Select size="sm" aria-label="Font size" value={editor.getAttributes("textStyle").fontSize || "14px"} onChange={event => editor.chain().focus().setFontSize(event.target.value).run()} options={FONT_SIZES} /></div>
      {[['bold', 'Bold', 'toggleBold'], ['italic', 'Italic', 'toggleItalic'], ['underline', 'Underline', 'toggleUnderline'], ['strike', 'Strikethrough', 'toggleStrike'], ['orderedList', 'Numbered list', 'toggleOrderedList'], ['bulletList', 'Bulleted list', 'toggleBulletList'], ['blockquote', 'Quote', 'toggleBlockquote']].map(([mark, label, command]) => <Button key={mark} size="sm" variant="ghost" selected={editor.isActive(mark)} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus()[command]().run()}>{label}</Button>)}
      <div className="w-40"><ColorInput aria-label="Text color" value={editor.getAttributes("textStyle").color || "#000000"} onChange={event => editor.chain().focus().setColor(event.target.value).run()} /></div>
      <div className="w-28"><Select size="sm" aria-label="Text alignment" value={['left', 'center', 'right'].find(textAlign => editor.isActive({ textAlign })) || 'left'} onChange={event => editor.chain().focus().setTextAlign(event.target.value).run()} options={['left', 'center', 'right'].map(value => ({ value, label: value }))} /></div>
      <Button variant="ghost" size="sm" selected={editor.isActive("link")} onClick={openLink}>Insert link</Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>Clear formatting</Button>
    </>;
  const linkModal = <Modal open={linkOpen} onClose={() => setLinkOpen(false)} title="Insert link" showClose layerClassName="z-[130]" footer={<><Button variant="ghost" onClick={() => { editor.chain().focus().unsetLink().run(); setLinkOpen(false); }}>Remove link</Button><Button onClick={applyLink}>Apply</Button></>}>
      <div className="space-y-3"><Field label="Display text" htmlFor="html-link-text"><Input id="html-link-text" value={displayText} onChange={event => setDisplayText(event.target.value)} /></Field><Field label="URL" htmlFor="html-link-url"><Input id="html-link-url" value={url} onChange={event => setUrl(event.target.value)} /></Field></div>
    </Modal>;
  if (variant === 'compose') return <div className="bun-html-editor bun-html-editor-compose flex min-h-[200px] flex-1 flex-col">
    {editor && <div role="toolbar" aria-label="Email formatting" className="flex shrink-0 flex-wrap items-center gap-1 border-b border-bun-line-soft px-[10px] py-1.5">
      {[['bold', 'Bold', 'toggleBold', <strong>B</strong>], ['italic', 'Italic', 'toggleItalic', <em>I</em>], ['underline', 'Underline', 'toggleUnderline', <u>U</u>], ['bulletList', 'Bulleted list', 'toggleBulletList', <BiListUl className="h-4 w-4" />], ['orderedList', 'Numbered list', 'toggleOrderedList', <BiListOl className="h-4 w-4" />]].map(([mark, label, command, icon]) => <Button key={mark} variant="ghost" iconOnly size="sm" aria-label={label} selected={editor.isActive(mark)} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus()[command]().run()}>{icon}</Button>)}
      <Button variant="ghost" iconOnly size="sm" aria-label="Insert link" onClick={openLink}><LinkIcon className="h-4 w-4" /></Button>
      <Popover><Popover.Button as={Button} ref={formattingRef} variant="ghost" size="sm" iconOnly aria-label="More formatting"><DotsHorizontalIcon className="h-4 w-4" /></Popover.Button><Popover.Panel portal anchorRef={formattingRef} width={300} maxHeight={400} zIndex={120} portalTarget={formattingRef.current?.closest('[data-testid="email-compose-layer"]')}><div className="flex flex-wrap gap-2 p-2">{extendedControls}</div></Popover.Panel></Popover>
      <div className="ml-auto">{toolbarActions}</div>
    </div>}
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto"><EditorContent editor={editor} className="flex min-h-[180px] grow shrink-0 flex-col" />{afterContent}</div>
    {linkModal}
  </div>;
  return <Card className="bun-html-editor"><EditorContent editor={editor} />{afterContent}{editor && <Card.Footer><div role="toolbar" aria-label="Email formatting" className="flex flex-wrap items-center gap-2">{extendedControls}</div></Card.Footer>}{linkModal}</Card>;
});
export default HtmlEditor;
